import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import fnc from '../../mymod/commFunction';
import {
  setClassCode,
  setHeaderInfo,
  setIsModal,
  setLessonDate,
  setLmsListCode,
} from '../../reduxToolkit/redux';

const getCourseGroups = (rows) => {
  const groupMap = new Map();

  rows.forEach((row) => {
    const groupCode = String(row.clGroupCode ?? 'etc');
    if (!groupMap.has(groupCode)) {
      groupMap.set(groupCode, {
        code: groupCode,
        name: row.groupName || '수업 그룹',
        classes: [],
      });
    }

    groupMap.get(groupCode).classes.push({
      code: row.clClassCode,
      name: row.clClassName,
      groupCode,
      groupName: row.groupName || '수업 그룹',
    });
  });

  return Array.from(groupMap.values());
};

const getLessons = (rows) => rows.map((row) => ({
  sno: row.sno,
  code: row.code,
  content: row.content,
  fileName: row.fileName,
  fileUrl: row.fileUrl,
  date: row.sDate,
  title: row.title,
  writer: row.writer,
}));

const isNewLesson = (date) => {
  if (!date) return false;
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return new Date(date) > oneWeekAgo;
};

const useDigitalLmsData = () => {
  const dispatch = useDispatch();
  const courseRefreshToken = useSelector((state) => state.reduxStore.isRefresh_studentCourseList);
  const [courseGroups, setCourseGroups] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isCourseLoading, setIsCourseLoading] = useState(true);
  const [isLessonLoading, setIsLessonLoading] = useState(false);
  const userName = fnc.getCookie('userName') || '학생';

  useEffect(() => {
    const acaCode = fnc.getCookie('acaCode');
    const stCode = fnc.getCookie('stCode');
    const db = fnc.getCookie('acaDbName');

    setIsCourseLoading(true);
    fnc.sendAxios('/select/lms/treeview', { acaCode, stCode, db }, (res) => {
      const groups = Array.isArray(res) ? getCourseGroups(res) : [];
      const nextClasses = groups.flatMap((group) => group.classes);
      setCourseGroups(groups);
      setSelectedClass((current) => (
        nextClasses.find((item) => String(item.code) === String(current?.code))
        || nextClasses[0]
        || null
      ));
      setIsCourseLoading(false);
    });
  }, [courseRefreshToken]);

  useEffect(() => {
    if (!selectedClass?.code) {
      setLessons([]);
      return;
    }

    dispatch(setClassCode(selectedClass.code));
    dispatch(setHeaderInfo({ title: selectedClass.name, direction: -1 }));
    dispatch(setIsModal(false));
    setSelectedLesson(null);
    setIsLessonLoading(true);
    const db = fnc.getCookie('acaDbName');

    fnc.sendAxios(
      '/select/lms/course/lessonSt',
      { classCode: selectedClass.code, kind: 'lesson', db },
      (res) => {
        setLessons(Array.isArray(res) ? getLessons(res) : []);
        setIsLessonLoading(false);
      },
    );
  }, [dispatch, selectedClass]);

  const selectClass = useCallback((classItem) => {
    setSelectedClass(classItem);
  }, []);

  const openLesson = useCallback((lesson) => {
    setSelectedLesson(lesson);
    dispatch(setLessonDate(lesson.date));
    dispatch(setLmsListCode(lesson.code));
    dispatch(setIsModal(true));
  }, [dispatch]);

  const closeLesson = useCallback(() => {
    setSelectedLesson(null);
    dispatch(setIsModal(false));
  }, [dispatch]);

  const allClasses = useMemo(
    () => courseGroups.flatMap((group) => group.classes),
    [courseGroups],
  );

  const newLessonCount = useMemo(
    () => lessons.filter((lesson) => isNewLesson(lesson.date)).length,
    [lessons],
  );

  return {
    allClasses,
    closeLesson,
    courseGroups,
    isCourseLoading,
    isLessonLoading,
    isNewLesson,
    lessons,
    newLessonCount,
    openLesson,
    selectClass,
    selectedClass,
    selectedLesson,
    userName,
  };
};

export default useDigitalLmsData;
